import React, { useState } from "react";
import { Icon, Tag, Tooltip, Button } from "antd";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClone } from "@fortawesome/free-solid-svg-icons";

import NumberFormat from "../../../NumberFormat";
import CircuitButton from "../../../customComponents/CircuitButton/CircuitButton";
import MultipleFacesButton from "../../../customComponents/MultipleFacesButton/MultipleFacesButton";
import SyncCircuitAlert from "../../../shared/SyncCircuit/SyncCircuitAlert";
import DeleteBundleModalComponent from "../../../DeleteBundleModalComponent";
import { BundleSvg } from "../../../../images/customIcons/BundleSvg";
import {
  belongsToBundle,
  displayIsCircuit,
  displayIsMultipleFaces,
  circuitHasMultipleFaces,
  getMultipleFacesCountInCircuit,
  getTotalMultipleFacesInCircuit,
} from "../../../../services/display";
import { isApprovedPaymentStatus } from "../../../../services/campaignManager";
import { getVenueCategory, getVenueTypeLabel } from "../../../../services/venueCategories";
import defaultImage from "../../../../images/bg.jpg";

// Same thumbnail derivation used in the table view.
const getThumb = (display) => {
  const pics = display.pictures;
  if (!pics || pics.length === 0 || !pics[0] || !pics[0].url) {
    return defaultImage;
  }
  return pics[0].url.split(".").slice(0, -1).join(".") + "-thumb.jpg";
};

const getPriceValue = (display, programmatic) =>
  programmatic === "programmatic" ? display.cpmi : display.price_per_day;

/**
 * Compact cart row for the redesigned cart drawer (LATIN-2537).
 *
 * Shows thumbnail + name + location, and — next to the price — the format tag
 * plus the circuit / multiple-faces badges (reusing the same CircuitButton /
 * MultipleFacesButton used in the table view). The X removes the screen.
 */
function CartScreenRow({
  display,
  programmatic,
  onRemove,
  cart,
  listBundle,
  removeBundlesFromCart,
  editCampaignActive,
  editCampaign,
}) {
  const { t } = useTranslation();
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);

  const matchBundle = belongsToBundle(display, listBundle);
  const isRemoveDisabled = editCampaignActive && isApprovedPaymentStatus(editCampaign);

  const handleDeleteModal = () => setDeleteModalVisible((visible) => !visible);

  const handleRemove = () => {
    if (matchBundle) {
      handleDeleteModal();
    } else {
      onRemove(display.id);
    }
  };

  const location = [
    display.administrative_area_level_2,
    display.administrative_area_level_1,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="cartScreenRow">
      <img className="cartRowThumb" src={getThumb(display)} alt="" />
      <div className="cartRowInfo">
        <div className="cartRowNameLine">
          <div className="cartRowName">{display.name}</div>
          {matchBundle ? (
            <Tooltip
              placement="top"
              title={
                <>
                  {t("This screen belongs to the following bundles.")}
                  {matchBundle.map((bundle) => (
                    <div key={bundle.id}>{bundle.name}</div>
                  ))}
                </>
              }
            >
              <Button
                className="cartRowBundleButton"
                shape="circle"
                size="small"
              >
                <Icon component={BundleSvg} className="cartRowBundleIcon" />
              </Button>
            </Tooltip>
          ) : null}
          {displayIsMultipleFaces(display) ? (
            <Tooltip
              placement="bottomLeft"
              title={`${t("Multiple faces screen")} (${
                display.mirror_screens_count
              })`}
            >
              <Button className="doubleScreen" shape="circle" size="small">
                <FontAwesomeIcon
                  icon={faClone}
                  size="20x"
                  className="iconTranslate"
                />
              </Button>
            </Tooltip>
          ) : displayIsCircuit(display) &&
            circuitHasMultipleFaces(display.secondary_displays) ? (
            <Tooltip
              placement="bottomLeft"
              title={
                <>
                  {t("Multiple faces screen")}:
                  <br />
                  {getMultipleFacesCountInCircuit(display.secondary_displays)}{" "}
                  {t("screens_abbreviated")} /{" "}
                  {getTotalMultipleFacesInCircuit(display.secondary_displays)}{" "}
                  {t("faces")}
                </>
              }
            >
              <Button className="doubleScreen" shape="circle" size="small">
                <FontAwesomeIcon
                  icon={faClone}
                  size="20x"
                  className="iconTranslate"
                />
              </Button>
            </Tooltip>
          ) : null}
          {display.display_type === "main" ? (
            <Tooltip
              placement="top"
              title={`${t("Circuit")} ${getVenueTypeLabel(display) || ""}`}
            >
              <Button
                shape="circle"
                size="small"
                className="screenCardCircuitButton"
              >
                <Icon type="deployment-unit" theme="outlined" />
              </Button>
            </Tooltip>
          ) : null}
          {display.sync_circuit && (
            <div className="screenCardSyncCircuitWrapper">
              <SyncCircuitAlert />
            </div>
          )}
        </div>
        {location ? <div className="cartRowLocation">{location}</div> : null}
        <div className="cartRowMeta">
          {getVenueTypeLabel(display) ? (
            <Tag
              className="cartRowFormatTag"
              color={getVenueCategory(display).color}
            >
              {getVenueTypeLabel(display)}
            </Tag>
          ) : null}
          <span className="cartRowPrice">
            <NumberFormat
              value={getPriceValue(display, programmatic)}
              currency={display.price_currency}
              include_currency={true}
            />
            {programmatic === "programmatic" ? " CPM" : `/${t("Day")}`}
          </span>
          {display.display_type === "main" && display.secondary_displays ? (
            <CircuitButton secondaryDisplays={display.secondary_displays} />
          ) : null}
          {displayIsMultipleFaces(display) ? (
            <MultipleFacesButton
              mirror_screens_count={display.mirror_screens_count}
            />
          ) : null}
        </div>
      </div>
      <DeleteBundleModalComponent
        deleteModalVisible={deleteModalVisible}
        matchBundle={matchBundle}
        handleDeleteModal={handleDeleteModal}
        cart={cart}
        listBundle={listBundle}
        removeBundlesFromCart={removeBundlesFromCart}
      />
        {!isRemoveDisabled && (
          <Tooltip
            title={
              matchBundle
                ? t(
                    "When deleting this screen, the packages to which it belongs, as well as the screens associated with those packages, will be automatically removed from the selection."
                  )
                : null
            }
          >
            <Icon type="close" className="cartRowRemove" onClick={handleRemove} />
          </Tooltip>
        )
      }
    </div>
  );
}

export default CartScreenRow;
